import { useCallback, useEffect, useRef, useState } from 'react';
import QrCode from '../../shared/QrCode.jsx';
import CodeInput from '../../shared/CodeInput.jsx';
import { notifySuccess } from '../../shared/toast.js';
import Modal from '../../shared/Modal.jsx';
import { Button } from '../../shared/uiKit.jsx';
import { useTokens } from '../../shared/theme.jsx';
import {
  beginAdminMfaEnrollment,
  challengeAdminMfa,
  confirmAdminMfaEnrollment,
  getAdminMfaStatus,
} from '../../shared/services/adminMfa.js';
import {
  MFA_CODE_LENGTH,
  adminMfaErrorCode,
  describeAdminMfaError,
  normalizeMfaCode,
  registerAdminMfaHandler,
  registerAdminMfaOpener,
} from '../../shared/services/adminMfaGate.js';

/**
 * Step-up MFA dialog shared by the super-admin console and institution portal.
 *
 * Mounted once, invisible until the axios interceptor hands it a 403. Two
 * modes behind one component, because they are two steps of the same job:
 *
 *   challenge — the admin has an authenticator; enter the current code.
 *   enroll    — no authenticator yet; take the key, then prove it works.
 *
 * Resolving true replays the request that was interrupted (see api.js), so a
 * lapsed elevation costs the admin a code and nothing else.
 *
 * QR code, added 2026-07-29, reversing the original "no rendering dependency"
 * call. Typing 32 base32 characters by hand is slow and error-prone, and it is
 * the first thing an admin does. The dependency (qrcode-generator, zero
 * transitive deps) renders inline SVG in the admin bundles only — it draws a string
 * the server already produced and already shows as text, verifies nothing, and
 * falls back to the key beside it if it fails. That is a different risk profile
 * from a dependency in the server's auth path, which totp.js still avoids.
 *
 * The setup key stays visible: scanning is not possible on every setup (a
 * desktop authenticator, a locked-down phone camera), and it is the fallback
 * when the QR fails to render.
 */

// Deliberately no duration in this copy, matching client/components/shared/
// AdminMfaModal.jsx. It used to promise "15 minutes", which was the code default
// in adminMfaService.js. That default is overridable, and production sets
// ADMIN_MFA_ELEVATION_TTL_SECONDS=86400 — so this dialog told every console and
// portal admin 15 minutes while the real window was 24 hours. Naming no number
// is the only version that cannot drift out of sync with the environment again.
// It is also the only accurate one: the window is inactivity-sliding
// (ADMIN_MFA_ELEVATION_SLIDING), so no fixed duration describes it.
const CHALLENGE_COPY = {
  title: 'Verify it\'s you',
  body: 'Enter the 6-digit code from your authenticator app. Admin actions stay unlocked for a limited time.',
  submit: 'Verify',
};

const ENROLL_COPY = {
  title: 'Set up your authenticator',
  body: 'Admin actions need a second factor. Scan this with your authenticator app — or add the key manually — then enter the code it shows.',
  submit: 'Confirm',
};

// Base32 in groups of four: the only way a hand-typed key stays checkable.
function formatSecret(secret) {
  return normalizeSecret(secret).replace(/(.{4})/g, '$1 ').trim();
}

function normalizeSecret(secret) {
  return String(secret || '').replace(/\s+/g, '');
}

export default function AdminMfaDialog() {
  const t = useTokens();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('challenge');
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  // Read inside the submit handler, which the input's onChange calls in the
  // same tick it sets state — the state value would still be the old one.
  const busyRef = useRef(false);
  // The interceptor is waiting on this; it must be called exactly once per
  // prompt, whatever closes the dialog.
  const resolverRef = useRef(null);
  // Set when the admin opened this from the account menu to REPLACE a working
  // authenticator. Replacing requires a live elevation, so the challenge comes
  // first and this says "do not close on success, go on to mint a new key".
  const replaceAfterChallengeRef = useRef(false);

  const startEnrollment = useCallback(async () => {
    setBusy(true);
    busyRef.current = true;
    setError('');
    setEnrollment(null);
    setCopied(false);
    try {
      const data = await beginAdminMfaEnrollment();
      setEnrollment({ secret: normalizeSecret(data?.secret), otpauthUri: data?.otpauthUri || '' });
    } catch (err) {
      setError(describeAdminMfaError(err));
    } finally {
      setBusy(false);
      busyRef.current = false;
    }
  }, []);

  useEffect(() => {
    return registerAdminMfaHandler((mfaCode) => {
      return new Promise((resolve) => {
        const enrolling = mfaCode === 'ADMIN_MFA_ENROLLMENT_REQUIRED';
        resolverRef.current = resolve;
        setMode(enrolling ? 'enroll' : 'challenge');
        setEnrollment(null);
        setCode('');
        setError('');
        setLocked(false);
        setCopied(false);
        setOpen(true);
        // Minting is kicked off here rather than from an effect on the open
        // state: an effect keyed on "enroll mode with no key yet" re-fires on
        // its own state churn and can mint twice for one prompt.
        if (enrolling) { startEnrollment(); }
      });
    });
  }, [startEnrollment]);

  // Opened deliberately from the account menu rather than by a refused request.
  // Which of the three states we land in depends on the account, so ask the
  // server instead of guessing: never enrolled goes straight to a new key; an
  // already-elevated session can replace one immediately; otherwise the
  // challenge runs first and chains into the replacement.
  useEffect(() => {
    return registerAdminMfaOpener(async () => {
      // A refused request already has the dialog open (and switches to enroll
      // itself when needed). Taking it over would drop that request's resolver
      // and leave it, and every later prompt, hanging.
      if (resolverRef.current) { return; }
      replaceAfterChallengeRef.current = false;
      setEnrollment(null);
      setCode('');
      setError('');
      setLocked(false);
      setCopied(false);
      setMode('challenge');
      setOpen(true);
      setBusy(true);
      busyRef.current = true;

      let status = null;
      try {
        status = await getAdminMfaStatus();
      } catch {
        // Fall through: treating an unreadable status as "already enrolled"
        // asks for a code, which is recoverable. Assuming the opposite would
        // offer to overwrite a working authenticator on a network blip.
        status = { enrolled: true, elevated: false };
      } finally {
        setBusy(false);
        busyRef.current = false;
      }

      if (!status?.enrolled || status?.elevated) {
        setMode('enroll');
        await startEnrollment();
        return;
      }
      replaceAfterChallengeRef.current = true;
    });
  }, [startEnrollment]);

  // Modal focuses inputRef directly now (initialFocusRef), so there is no
  // "focus the first node, then yank it back" race to correct. This only
  // re-asserts focus when the enroll key ARRIVES, since the field mounts before
  // the mint resolves.
  useEffect(() => {
    if (open && !locked && enrollment) { inputRef.current?.focus(); }
  }, [open, locked, enrollment]);

  const settle = useCallback((elevated) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(elevated);
    setOpen(false);
    setEnrollment(null);
    setCode('');
    setError('');
    setBusy(false);
    busyRef.current = false;
  }, []);

  const submit = useCallback(async (value) => {
    const entered = normalizeMfaCode(value);
    if (entered.length !== MFA_CODE_LENGTH || busyRef.current || locked) { return; }

    setBusy(true);
    busyRef.current = true;
    setError('');
    // Set inside the catch, acted on after the finally clears `busy` — minting
    // a key sets `busy` itself and must not be undone on the way out.
    let needsKey = false;
    try {
      if (mode === 'enroll') {
        await confirmAdminMfaEnrollment(entered);
        // The dialog closes on success, so without this the only feedback for
        // "your authenticator is now set up" is the dialog vanishing.
        notifySuccess('Authenticator set up. Admin actions are unlocked.');
        settle(true);
        return;
      }

      await challengeAdminMfa(entered);

      // Verified in order to replace: stay open and mint the new key instead of
      // closing. Cleared first so a later failure cannot loop back through here.
      if (replaceAfterChallengeRef.current) {
        replaceAfterChallengeRef.current = false;
        setMode('enroll');
        setCode('');
        setEnrollment(null);
        needsKey = true;
      } else {
        notifySuccess('Verified. Admin actions are unlocked.');
        settle(true);
        return;
      }
    } catch (err) {
      const failure = adminMfaErrorCode(err);
      setError(describeAdminMfaError(err));
      setCode('');
      if (failure === 'ADMIN_MFA_LOCKED_OUT') {
        setLocked(true);
      }
      // The pending secret expired. Keep the admin in enroll mode, but make
      // them ask for a fresh key rather than silently minting another.
      if (failure === 'ADMIN_MFA_NO_PENDING_ENROLLMENT') {
        setEnrollment(null);
      }
      // The account turned out to have no authenticator at all, so the code
      // prompt was the wrong step. Switch to enrollment and mint a key.
      if (failure === 'ADMIN_MFA_ENROLLMENT_REQUIRED') {
        setMode('enroll');
        setEnrollment(null);
        needsKey = true;
      }
    } finally {
      setBusy(false);
      busyRef.current = false;
    }

    if (needsKey) { await startEnrollment(); }
  }, [locked, mode, settle, startEnrollment]);

  const onCodeChange = (event) => {
    const next = normalizeMfaCode(event.target.value);
    setCode(next);
    if (error) { setError(''); }
    if (next.length === MFA_CODE_LENGTH) { submit(next); }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(enrollment.secret);
      setCopied(true);
    } catch {
      setError('Copy failed. Select the key and copy it by hand.');
    }
  };

  if (!open) { return null; }

  const copy = mode === 'enroll' ? ENROLL_COPY : CHALLENGE_COPY;
  const canSubmit = code.length === MFA_CODE_LENGTH && !busy && !locked;

  return (
    <Modal
      open={open}
      onClose={() => { settle(false); }}
      title={copy.title}
      initialFocusRef={inputRef}
    >
      <p className="text-[14px] leading-5" style={{ fontFamily: t.fonts.body, color: t.body }}>
        {copy.body}
      </p>

      {mode === 'enroll' ? (
        <div className="mt-3">
          {enrollment ? (
            <>
              {enrollment.otpauthUri ? (
                <div className="mb-3 flex justify-center">
                  {/* White plate around the code: scanners need the light
                      modules light, and this dialog renders on near-black. */}
                  <div className="rounded-md bg-white p-2">
                    <QrCode
                      value={enrollment.otpauthUri}
                      size={160}
                      title="Scan this with your authenticator app"
                    />
                  </div>
                </div>
              ) : null}
              <div
                className="rounded-md border px-3 py-2"
                style={{ backgroundColor: t.statBg, borderColor: t.border }}
              >
                <div
                  className="text-[10px] uppercase tracking-wider"
                  style={{ fontFamily: t.fonts.mono, color: t.muted }}
                >
                  Setup key
                </div>
                <div
                  className="mt-1 break-all text-[14px] leading-5"
                  style={{ fontFamily: t.fonts.mono, color: t.heading }}
                >
                  {formatSecret(enrollment.secret)}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                  small
                  variant="secondary"
                  label={copied ? 'Copied' : 'Copy key'}
                  onClick={copySecret}
                />
                {enrollment.otpauthUri ? (
                  <a
                    href={enrollment.otpauthUri}
                    className={`rounded text-[13px] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
                    style={{ fontFamily: t.fonts.body, color: t.muted }}
                  >
                    Open in your authenticator app
                  </a>
                ) : null}
              </div>
              <p className="mt-2 text-[12px] leading-4" style={{ fontFamily: t.fonts.body, color: t.muted }}>
                This key is shown once, and is only stored after a code confirms it.
              </p>
            </>
          ) : (
            <p className="text-[13px]" style={{ fontFamily: t.fonts.body, color: t.muted }}>
              {busy ? 'Minting a setup key…' : 'No setup key yet.'}
            </p>
          )}
        </div>
      ) : null}

      <label
        className="mt-4 block text-[10px] uppercase tracking-wider"
        style={{ fontFamily: t.fonts.mono, color: t.muted }}
        htmlFor="admin-mfa-code"
      >
        6-digit code
      </label>
      <CodeInput
        id="admin-mfa-code"
        ref={inputRef}
        value={code}
        onChange={onCodeChange}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit(code);
          }
        }}
        length={MFA_CODE_LENGTH}
        disabled={locked}
        invalid={Boolean(error)}
        ariaDescribedBy={error ? 'admin-mfa-error' : undefined}
        t={t}
      />

      {/* Always rendered, so an error cannot shove the buttons below it down.
          min-h holds one line; role="alert" still announces on appearance
          because the TEXT changes, not the element. */}
      <p
        id="admin-mfa-error"
        role="alert"
        className="mt-2 min-h-[18px] text-[13px] leading-[18px]"
        style={{ fontFamily: t.fonts.body, color: t.danger }}
      >
        {error}
      </p>

      <div className="mt-4 flex items-center justify-end gap-2">
        {mode === 'enroll' && !busy ? (
          <Button small variant="secondary" label="New key" onClick={startEnrollment} />
        ) : null}
        <Button small variant="secondary" label="Cancel" onClick={() => { settle(false); }} disabled={busy} />
        <Button small label={busy ? 'Checking…' : copy.submit} onClick={() => { submit(code); }} disabled={!canSubmit} />
      </div>
    </Modal>
  );
}
